'use client';

import { useCallback, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import {
  EntityRatingCommentCount,
  Laboratory,
  LaboratoryExam,
  SimilarLaboratoriesResponse,
} from '@medprev-web-app/api-graphql-client';

import { BranchLaboratoryHeader } from './branch-laboratory-header.component';
import { BranchLaboratoryAsideCard } from './branch-laboratory-aside-card.component';
import { LaboratoryOthersLaboratories } from './laboratory-others-laboratories.component';
import { LaboratoryExamsFlow } from './laboratory-exams-flow/laboratory-exams-flow.component';
import { useLaboratoryExamSelection } from './laboratory-exams-flow/_hooks/use-laboratory-exam-selection.hook';
import { LaboratoryExamsFlowKeys } from './laboratory-exams-flow/laboratory-exams-flow.types';
import { BranchLaboratoryExamsAction } from './branch-laboratory-exams-action.component';
import { BranchLaboratoryRatingSection } from './branch-laboratory-rating-section.component';

import { ExamsCoverageBanner } from '@/app/(main)/(pages)/_components/exams-coverage/exams-coverage-banner.component';
import {
  SelectedExamsParam,
  useSelectedExamsCoverage,
} from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_hooks/use-selected-exams-coverage.hook';
import { buildLaboratoryExamsCoverage } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_utils/build-laboratory-exams-coverage.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { Breadcrumb } from '@/components/ui/breadcrumb.component';
import { Card } from '@/components/ui/card.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { EntitySectionDivider } from '@/app/(main)/(pages)/_components/entities/entity-section-divider.component';
import { EntityActionLoading } from '@/app/(main)/(pages)/_components/entities/entity-action-loading.component';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';
import { ErrorBoundary } from '@/app/_components/error-boundary.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { EntityCommentsSort } from '@/app/(main)/(pages)/_components/entities/entity-comments-sort.component';
import { useEntityComments } from '@/app/(main)/(pages)/_components/entities/hooks/use-entity-comments.hook';
import { getLaboratoryProfileComments } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-profile-from-search-api.service';
import { SearchApiRatingCommentsOrderBy } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { useEntityProfile } from '@/app/(main)/(pages)/_components/entities/hooks/use-entity-profile.hook';
import {
  getLaboratoryProfileFromSearchApi,
  LaboratoryProfileFromSearchApi,
} from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-profile-from-search-api.service';
import { TabLaboratory } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/tab-laboratory.component';
import { RatingComment } from '@/types';
import { EntityProfileActions } from '@/app/(main)/(pages)/_components/entities/entity-profile-actions.component';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';

interface LaboratoryWrapperProps {
  laboratory: Laboratory;
  commentsCount?: EntityRatingCommentCount | null;
  initialComments?: RatingComment[];
  exams: LaboratoryExam[] | null;
  similarLaboratories: SimilarLaboratoriesResponse | null;
  selectedExams?: SelectedExamsParam;
}

export function BranchLaboratoryWrapper({
  commentsCount,
  laboratory: laboratoryFromBff,
  initialComments,
  exams,
  similarLaboratories,
  selectedExams,
}: LaboratoryWrapperProps) {
  const { value: newScheduleEnabled, loading: isFlagLoading } = useFeatureFlag('newSchedule');
  const usesLegacyLayout = isFlagLoading || !newScheduleEnabled;
  const usesSearchApi = !usesLegacyLayout && Boolean(laboratoryFromBff.slug);

  const { profile } = useEntityProfile<LaboratoryProfileFromSearchApi>(laboratoryFromBff.slug ?? '', {
    fetchProfile: getLaboratoryProfileFromSearchApi,
    fallbackProfile: () => ({
      laboratory: laboratoryFromBff,
      commentsCount: commentsCount ?? { total: 0, comments: [] },
      initialComments: initialComments ?? [],
      hasMoreComments: false,
    }),
    loggerContext: 'laboratory.profile-view',
    enabled: usesSearchApi,
  });

  const usesSearchApiProfile = usesSearchApi && Boolean(profile);
  const laboratory = profile?.laboratory ?? laboratoryFromBff;

  const laboratoryName = resolveLaboratoryName(laboratory);
  const facilityId = laboratory.id ?? '';

  const { coverage } = useSelectedExamsCoverage(facilityId, selectedExams);
  const selection = useLaboratoryExamSelection(coverage?.offered);
  const [flowIsOpen, setFlowIsOpen] = useState(false);
  const [flowInitialKey, setFlowInitialKey] = useState(LaboratoryExamsFlowKeys.EXAMS);

  const {
    orderBy,
    comments,
    hasMore: hasMoreComments,
    commentsKey,
    isSorting: isSortingComments,
    changeOrderBy,
  } = useEntityComments(
    useCallback(
      (page: number, nextOrderBy: SearchApiRatingCommentsOrderBy) =>
        getLaboratoryProfileComments(facilityId, page, nextOrderBy),
      [facilityId],
    ),
    profile?.initialComments ?? [],
    Boolean(profile?.hasMoreComments),
    { context: 'laboratory.comments', entity: { facility_id: facilityId } },
  );

  const loadCommentsPage = useCallback(
    (page: number) => getLaboratoryProfileComments(facilityId, page, orderBy),
    [facilityId, orderBy],
  );

  const showsCoverage = !usesLegacyLayout && Boolean(coverage);
  const showsSchedulesAction = showsCoverage && Number(coverage?.matched) > 0;
  const showsOthersLaboratories = !usesLegacyLayout && Number(similarLaboratories?.total) > 0;

  const othersLaboratories = similarLaboratories && (
    <>
      <Typography variant={TypographyVariant.MEDIUM_SUBTITLE} as="h2" className="mb-1 text-center">
        Outros postos de coleta
      </Typography>

      <LaboratoryOthersLaboratories
        laboratory={laboratory}
        initialFetchResponse={similarLaboratories}
        align="center"
        selectedExams={selectedExams}
      />
    </>
  );

  const openFlow = (key: LaboratoryExamsFlowKeys) => {
    setFlowInitialKey(key);
    setFlowIsOpen(true);
  };

  const handleViewCoverage = () => {
    if (coverage) {
      getAnalyticsPlugin().track(AnalyticsEnum.LABORATORY_PROFILE_CLICK_COVERAGE_VIEW, {
        nome_laboratorio: laboratoryName,
        exames_total: coverage.total,
        exames_atendidos: coverage.matched,
        cobertura_completa: coverage.isFullCoverage,
      });
    }

    openFlow(LaboratoryExamsFlowKeys.COVERAGE);
  };

  const coverageBanner = showsCoverage && coverage
    ? (
        <ExamsCoverageBanner
          matched={coverage.matched}
          total={coverage.total}
          isFullCoverage={coverage.isFullCoverage}
          dataTestId={DataTestIdEnum.BRANCH_LABORATORY_COVERAGE_BANNER}
          onViewCoverage={handleViewCoverage}
        />
      )
    : undefined;

  const laboratoryActions = (
    <EntityProfileActions
      entityType={EntityTypeEnum.LABORATORY_COLLECT_POINT}
      entity={laboratory}
      scheduleAction={isFlagLoading
        ? <EntityActionLoading />
        : (
            <BranchLaboratoryExamsAction
              laboratoryId={facilityId}
              usesLegacyRedirect={!newScheduleEnabled}
              showsSchedules={showsSchedulesAction}
              onSelectExams={() => openFlow(LaboratoryExamsFlowKeys.EXAMS)}
              onSeeSchedules={() => openFlow(LaboratoryExamsFlowKeys.SCHEDULES)}
            />
          )}
      scheduleActionOnDesktop={newScheduleEnabled ? 'always' : 'until-xl'}
      shareLabel="Compartilhar"
    />
  );

  const breadcrumbsItems = [
    {
      label: 'Rede de atendimento',
      href: '/rede-de-atendimento/',
    },
    {
      label: laboratoryName,
    },
  ];

  return (
    <>
      <Breadcrumb list={breadcrumbsItems} className="hidden sm:block" />
      <Breadcrumb list={breadcrumbsItems} abbreviated className="sm:hidden" />

      <ErrorBoundary loggerContext="laboratory.wrapper">
        <ContentContainer className="md:py-6 bg-white md:bg-transparent">
          <div className="block mx-auto sm:max-w-lg md:max-w-full md:w-auto xl:grid xl:grid-flow-col xl:auto-cols-fr xl:items-start xl:gap-6">
            <section>
              <Card className="px-0 border-0 md:border border-gray-200 md:px-10 pt-6 pb-6 md:pt-8 md:pb-8 3xl:pt-10 3xl:pb-10 w-full">
                <BranchLaboratoryHeader
                  laboratory={laboratory}
                  commentsCount={usesSearchApiProfile ? profile?.commentsCount : commentsCount}
                  coverageBanner={coverageBanner}
                  actions={laboratoryActions}
                />
                <TabLaboratory
                  laboratory={laboratory}
                  initialSimilarLaboratories={usesLegacyLayout ? similarLaboratories : null}
                  rating={(
                    <BranchLaboratoryRatingSection
                      laboratory={laboratory}
                      countComments={usesSearchApiProfile ? profile?.commentsCount : commentsCount}
                      initialComments={usesSearchApiProfile ? comments : initialComments}
                      hasSimilarLaboratories={Number(similarLaboratories?.total) > 0}
                      loadCommentsPage={usesSearchApiProfile ? loadCommentsPage : undefined}
                      hasMoreComments={usesSearchApiProfile && hasMoreComments}
                      commentsKey={usesSearchApiProfile ? commentsKey : undefined}
                      isSortingComments={usesSearchApiProfile && isSortingComments}
                      commentsSort={usesSearchApiProfile
                        ? (
                            <EntityCommentsSort
                              orderBy={orderBy}
                              onOrderByChange={changeOrderBy}
                              disabled={isSortingComments}
                            />
                          )
                        : undefined}
                    />
                  )}
                />

                {showsOthersLaboratories && (
                  <div className="pb-3 md:pb-8 xl:hidden">
                    <EntitySectionDivider />

                    {othersLaboratories}
                  </div>
                )}
              </Card>
            </section>

            {usesLegacyLayout
              ? (
                  <BranchLaboratoryAsideCard
                    laboratory={laboratory}
                    exams={exams}
                    className="hidden xl:block border-gray-200"
                  />
                )
              : showsOthersLaboratories && (
                <Card className="hidden xl:block w-full rounded-xl px-10 py-8 xl:sticky xl:top-6 border-gray-200">
                  {othersLaboratories}
                </Card>
              )}
          </div>
        </ContentContainer>
      </ErrorBoundary>

      {!usesLegacyLayout && (
        <LaboratoryExamsFlow
          laboratory={laboratory}
          laboratoryName={laboratoryName}
          facilityId={facilityId}
          coverage={coverage ? buildLaboratoryExamsCoverage(laboratoryName, coverage) : undefined}
          selection={selection}
          open={flowIsOpen}
          initialKey={flowInitialKey}
          onOpenChange={setFlowIsOpen}
        />
      )}
    </>
  );
}
